import React, { createContext, useContext, useState, useEffect } from "react";
import { socketService } from "../../../services/socket";
import { supabase } from "../../../services/supabase";
import { Sentry } from "../../../sentry";
import config from "../../../config";

export interface User {
  id: string;
  name: string;
  role: string;
  image: string;
}

interface AuthContextType {
  user: User | null;
  login: (name: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (password: string) => Promise<{ success: boolean; error?: string }>;

  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper: verify user profile via backend HTTP call (bypasses RLS, always available)
  const fetchAndSetProfile = async (
    email: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.debug(`🔐 [Auth] Verifying profile via HTTP for: ${email}`);

      const backendUrl = config.API_URL || window.location.origin;

      // Small delay to let Supabase auth trigger finish writing to public.users
      await new Promise((r) => setTimeout(r, 1000));

      const response = await fetch(
        `${backendUrl}/api/auth/verify-profile?email=${encodeURIComponent(email)}`,
      );
      const result = await response.json();

      if (!result.success || !result.profile) {
        console.warn(`🚫 [Auth] Not authorized: ${email}`);
        await supabase.auth.signOut();
        return {
          success: false,
          error: result.error || "Tu cuenta no está autorizada. Contacta con el administrador.",
        };
      }

      const profile = result.profile;
      if (!profile.app_role) {
        console.warn(`🚫 [Auth] No assigned role for ${email}`);
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Tu cuenta no tiene un rol asignado. Contacta con el administrador.",
        };
      }
      const userName = profile.name || email.split("@")[0];
      const realUser: User = {
        id: profile.id,
        name: userName,
        role: profile.app_role,
        image:
          profile.image ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random&color=fff`,
      };

      console.debug(`✅ [Auth] Profile verified: ${realUser.name} (${realUser.role})`);
      setUser(realUser);
      localStorage.setItem("auth_user", JSON.stringify(realUser));

      // Connect socket
      if (!socketService.connected) socketService.connect();
      socketService.emit("identify-logger", { name: realUser.role });

      return { success: true };
    } catch (e: any) {
      console.error("[Auth] Profile fetch error:", e);
      return { success: false, error: e.message || "Error fetching profile" };
    }
  };

  useEffect(() => {
    // 1. Restore session from localStorage (fast, avoids flash) then revalidate.
    //    We keep `isLoading` true until revalidation resolves so the app shows
    //    <LoadingScreen/> instead of a half-authenticated UI (e.g. ProjectSelector
    //    with the "Select a mission project" header but no projects loaded).
    let revalidationPromise: Promise<void> | null = null;
    const savedUser = localStorage.getItem("auth_user");
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        socketService.connect();

        // In E2E mode the session is pre-injected and the verify-profile route is
        // intercepted — skip async Supabase revalidation to avoid race conditions
        // that clear the user mid-test under parallel load.
        if (localStorage.getItem("__e2e__") !== "1") {
          // Revalidate session against Supabase to prevent role tampering.
          // We attempt refreshSession() before giving up — this covers the
          // common case where a deploy causes a full page reload and the
          // short-lived access token has expired but the refresh token is
          // still valid.
          const revalidateSession = async (attempt = 1): Promise<void> => {
            try {
              let {
                data: { session },
              } = await supabase.auth.getSession();

              // If no valid session, try refreshing the token
              if (!session?.user?.email) {
                console.debug("[Auth] No active session, attempting token refresh…");
                const { data: refreshData } = await supabase.auth.refreshSession();
                session = refreshData.session;
              }

              if (!session?.user?.email) {
                // Both getSession and refreshSession failed — truly expired
                console.warn("[Auth] Session expired after refresh attempt, logging out");
                localStorage.removeItem("auth_user");
                setUser(null);
                socketService.disconnect();
                return;
              }

              // Session is valid — re-fetch authoritative profile from backend
              const result = await fetchAndSetProfile(session.user.email);
              if (!result.success) {
                localStorage.removeItem("auth_user");
                setUser(null);
                socketService.disconnect();
              }
            } catch (err) {
              // Network error (backend restarting during deploy) — retry once
              if (attempt < 2) {
                console.debug(
                  `[Auth] Revalidation failed (attempt ${attempt}), retrying in 2s…`,
                  err,
                );
                await new Promise((r) => setTimeout(r, 2000));
                return revalidateSession(attempt + 1);
              }
              // After retries, if we still fail, it's safer to clear the session
              // rather than trapping the user in a pseudo-authenticated state where
              // sockets are rejected and projects never load.
              console.warn(
                "[Auth] Revalidation failed after retries, logging out to prevent stuck state",
                err,
              );
              localStorage.removeItem("auth_user");
              setUser(null);
              socketService.disconnect();
            }
          };
          revalidationPromise = revalidateSession();
        }
      } catch (e) {
        console.error("Failed to parse saved user", e);
        localStorage.removeItem("auth_user");
        setUser(null);
      }
    }

    // 2. Dev auto-login removed for security — use normal login flow

    // 3. Listen for OAuth redirects (Google callback)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.debug(`🔐 [Auth] State change: ${event}`);

      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session?.user?.email) {
        // Only process if we don't already have a user (avoid double-processing)
        const currentUser = localStorage.getItem("auth_user");
        if (!currentUser) {
          console.debug(`🔐 [Auth] OAuth callback for: ${session.user.email}`);
          const result = await fetchAndSetProfile(session.user.email);
          if (!result.success) {
            console.warn(`🚫 [Auth] OAuth user not authorized: ${session.user.email}`);
            // Store error for Login component to display
            localStorage.setItem("auth_error", result.error || "Not authorized");
          }
        }
      }
    });

    // Only drop the splash once revalidation has settled (success or fail).
    // If there was no stale localStorage session, drop it immediately.
    if (revalidationPromise) {
      revalidationPromise.finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }

    // 4. Tab-wake & periodic revalidation — catches sessions that expired
    //    while the tab was idle. Without this, the user keeps a "logged in"
    //    UI with an invalid Supabase token, and silent socket failures
    //    follow (logs queued forever, ACKs that never come, etc.).
    let lastCheck = Date.now();
    const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 min

    const revalidateSilently = async () => {
      // Skip when no user (already logged out) or E2E mode
      if (!localStorage.getItem("auth_user")) return;
      if (localStorage.getItem("__e2e__") === "1") return;
      try {
        let {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user?.email) {
          const { data: refreshData } = await supabase.auth.refreshSession();
          session = refreshData.session;
        }
        if (!session?.user?.email) {
          console.warn("[Auth] Background revalidation found expired session — logging out");
          localStorage.removeItem("auth_user");
          setUser(null);
          socketService.disconnect();
        }
      } catch (err) {
        // Network errors here are tolerable — don't force logout, the user
        // will be re-checked on next visibility/interval tick.
        console.debug("[Auth] Background revalidation skipped due to network error:", err);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) return;
      // Coalesce: only revalidate if it's been at least 1 min since last check
      if (Date.now() - lastCheck < 60_000) return;
      lastCheck = Date.now();
      revalidateSilently();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = setInterval(() => {
      lastCheck = Date.now();
      revalidateSilently();
    }, REVALIDATE_INTERVAL_MS);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(intervalId);
    };
  }, []);

  // Email + Password login (existing flow)
  const login = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("No user data returned");

      return await fetchAndSetProfile(email);
    } catch (e: any) {
      const rawMsg = e?.message || "";
      const isNetwork =
        rawMsg === "Failed to fetch" ||
        e?.name === "AuthRetryableFetchError" ||
        e?.name === "TypeError" ||
        /network|fetch|offline|ERR_/i.test(rawMsg);

      const supabaseUrl = config.SUPABASE_URL || "";
      const supabaseHost = (() => {
        try {
          return supabaseUrl ? new URL(supabaseUrl).host : "";
        } catch {
          return "";
        }
      })();

      const diag = {
        scope: "auth.login",
        email,
        errorName: e?.name,
        errorMessage: rawMsg,
        errorStatus: e?.status,
        kind: isNetwork ? "network" : "auth",
        online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        supabaseHost,
        href: typeof window !== "undefined" ? window.location.href : undefined,
      };

      console.error(`[auth] Login failed (${diag.kind}) — ${rawMsg || "unknown error"}`, diag, e);

      try {
        Sentry.captureException(e, {
          tags: {
            scope: "auth.login",
            login_failure_kind: diag.kind,
            supabase_host: supabaseHost,
            navigator_online: String(diag.online),
          },
          extra: diag,
        });
      } catch {
        /* never let telemetry break the login flow */
      }

      const friendly = isNetwork
        ? "No pudimos conectar con el servidor. Revisa tu conexión, VPN o extensiones del navegador e intenta de nuevo."
        : rawMsg || "Authentication failed";
      return { success: false, error: friendly };
    }
  };

  // Google OAuth login
  const loginWithGoogle = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      // Clear any previous auth errors and stale sessions
      localStorage.removeItem("auth_error");
      localStorage.removeItem("auth_user");
      console.debug("🔐 [Auth] Clearing stale session before OAuth...");
      await supabase.auth.signOut();

      console.debug("🔐 [Auth] Starting Google OAuth redirect...");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin + "/",
          queryParams: {
            prompt: "select_account",
          },
        },
      });

      console.debug("🔐 [Auth] OAuth result:", { data, error });
      if (error) throw error;

      // Supabase will redirect the page to Google automatically
      return { success: true };
    } catch (e: any) {
      console.error("Google login failed:", e);
      return {
        success: false,
        error: e.message || "Google authentication failed",
      };
    }
  };

  // Password Reset Request
  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/update-password",
      });
      if (error) throw error;
      return { success: true };
    } catch (e: any) {
      console.error("Reset password failed:", e);
      return {
        success: false,
        error: e.message || "No se pudo enviar el correo de recuperación",
      };
    }
  };

  // Update Password (after recovery link)
  const updatePassword = async (
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      return { success: true };
    } catch (e: any) {
      console.error("Update password failed:", e);
      return {
        success: false,
        error: e.message || "No se pudo actualizar la contraseña",
      };
    }
  };

  const logout = () => {
    // Sign out from Supabase Auth (clears OAuth session too)
    supabase.auth.signOut().catch((err) => console.warn("[Auth] Supabase signOut error:", err));
    socketService.disconnect();
    setUser(null);
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_error");
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginWithGoogle,
        resetPassword,
        updatePassword,
        logout,
        isAuthenticated,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
